import type {
  TranscriptElement,
  TranscriptTextElement,
} from '../../types.ts/transcript';
import { TimelinePositionPropsNotNull } from '../types';
import { VideoElement } from './elements/VideoElement';

/**
 * Type for transcript modifications
 */
export type TranscriptModification = {
  id: string; // ID of the transcript element being modified
  type: 'replaceText' | 'mute' | 'addPunctuation' | 'removePunctuation'; // Type of modification
  value: any; // New value (e.g., replacement text, boolean for mute)
};

/**
 * Transcript class that manages the original transcript and active transcript derivation
 */
export class LegacyTranscript {
  /**
   * The original, immutable transcript elements
   * This is never modified directly - it serves as a reference point
   */
  private originalTranscription: readonly TranscriptElement[];

  /**
   * Array of modifications to apply to the transcript elements
   * These are overlaid on the active transcript without changing the original
   */
  private transcriptModifications: TranscriptModification[];

  /**
   * Creates a new Transcript instance
   * @param original The original transcript elements
   */
  constructor(original: TranscriptElement[]) {
    // Create a deep copy and normalize timestamps & initial index (word_index deprecated)
    const updatedTranscription = original.map((element, idx) => {
      if (LegacyTranscript.isNotRemovedTextElement(element)) {
        return {
          ...element,
          ts: Math.round(element.ts * 10000) / 10000,
          end_ts: Math.round(element.end_ts * 10000) / 10000,
          initial_index: idx,
        };
      }
      return { ...element }; // Ensure deep copy for all elements
    });
    this.originalTranscription = Object.freeze(updatedTranscription);
    this.transcriptModifications = [];

    // Initialize the inferred transcript helper
    // this.inferredTranscriptHelper = new InferredTranscript(
    //   this.originalTranscription,
    // );
  }

  /**
   * Get word_index from initial_index array. Hack for existing transcript functions.
   * @param startIndex The start index of the word in the original transcript
   * @param The end index of the word in the original transcript
   * @returns Array<string> of word indexes
   */
  getWordIndexFromIndexes(startIndex: number, endIndex: number): string[] {
    const wordIndexes: string[] = [];
    for (let i = startIndex; i <= endIndex; i++) {
      const element = this.originalTranscription[i];
      if (element.type === 'text' && element.word_index !== undefined) {
        wordIndexes.push(element.word_index.toString());
      }
    }
    return wordIndexes;
  }

  /**
   * Gets the original transcript elements
   * @returns The original transcript elements (immutable)
   */
  getOriginalTranscription(): ReadonlyArray<TranscriptElement> {
    return this.originalTranscription;
  }

  /**
   * Gets the active transcript based on video elements
   * @param videoElements Array of video elements to determine active transcript.
   * @returns Array of active transcript elements with modifications applied
   */
  getActiveTranscript(videoElements: VideoElement[]): TranscriptElement[] {
    const sortedElements = videoElements.sort((a, b) => a.time - b.time);

    const activeWords = sortedElements.flatMap((ve: VideoElement) => {
      const seg = ve.getTranscriptSegment();
      return seg?.getWordsWithTimestamps() || [];
    });

    const activeWordsWithPunctuation =
      this.includeRelevantPunctuation(activeWords);
    return this.applyModifications(activeWordsWithPunctuation);
  }

  /**
   * Determines if a word is in the active transcript
   * @param wordId The word_index of the word to check
   * @param activeTranscript The current active transcript
   * @returns Boolean indicating if the word is in the active transcript
   */
  isWordInActiveTranscript(
    wordId: string,
    activeTranscript: TranscriptElement[],
  ): boolean {
    return activeTranscript.some(
      word =>
        word.type === 'text' &&
        word.word_index !== undefined &&
        word.word_index.toString() === wordId,
    );
  }

  /**
   * Determines if a word can be removed
   * @param wordId The word_index of the word to check
   * @param activeTranscript The current active transcript
   * @returns Boolean indicating if the word can be removed
   */
  canWordBeRemoved(
    wordId: string,
    activeTranscript: TranscriptElement[],
  ): boolean {
    // A word can be removed if it's in the active transcript
    return this.isWordInActiveTranscript(wordId, activeTranscript);
  }

  /**
   * Determines if a word can be restored
   * @param wordId The word_index of the word to check
   * @param activeTranscript The current active transcript
   * @returns Boolean indicating if the word can be restored
   */
  canWordBeRestored(
    wordId: string,
    activeTranscript: TranscriptElement[],
  ): boolean {
    // A word can be restored if it's in the original transcript but not in the active transcript
    const originalElement = this.getElementByWordIndex(wordId);
    if (!originalElement) return false;

    return !this.isWordInActiveTranscript(wordId, activeTranscript);
  }

  /**
   * Adds a modification to the transcript
   * @param modification The modification to add
   */
  addModification(modification: TranscriptModification): void {
    // Check if there's an existing modification for this element
    const existingIndex = this.transcriptModifications.findIndex(
      mod => mod.id === modification.id && mod.type === modification.type,
    );

    if (existingIndex !== -1) {
      // Replace the existing modification
      this.transcriptModifications[existingIndex] = modification;
    } else {
      // Add a new modification
      this.transcriptModifications.push(modification);
    }
  }

  /**
   * Removes a modification
   * @param id ID of the transcript element
   * @param type Type of modification to remove
   * @returns true if a modification was removed, false otherwise
   */
  removeModification(
    id: string,
    type: TranscriptModification['type'],
  ): boolean {
    const initialLength = this.transcriptModifications.length;
    this.transcriptModifications = this.transcriptModifications.filter(
      mod => !(mod.id === id && mod.type === type),
    );
    return initialLength > this.transcriptModifications.length;
  }

  /**
   * Gets all modifications
   * @returns Array of transcript modifications
   */
  getModifications(): TranscriptModification[] {
    return [...this.transcriptModifications];
  }

  /**
   * Clears all modifications
   */
  clearModifications(): void {
    this.transcriptModifications = [];
  }

  /**
   * Includes punctuation elements that are associated with active words
   */
  private includeRelevantPunctuation(
    activeWords: TranscriptElement[],
  ): TranscriptElement[] {
    if (activeWords.length === 0) return [];

    // Build a fast lookup map from original transcript words to their indices (O(N)).
    // Key uses type+value+ts for parity with the prior findIndex predicate.
    const keyForText = (el: TranscriptTextElement): string =>
      `text|${el.value}|${el.ts}`;

    const indexByKey = new Map<string, number>();
    for (let i = 0; i < this.originalTranscription.length; i++) {
      const el = this.originalTranscription[i];
      if (el.type === 'text') {
        const k = keyForText(el as TranscriptTextElement);
        // Preserve first occurrence semantics (findIndex behavior)
        if (!indexByKey.has(k)) indexByKey.set(k, i);
      }
    }

    // Get the indices of active words in the original transcription (O(A)).
    const activeIndices = activeWords.map(word => {
      if (word.type === 'text') {
        const idx = indexByKey.get(keyForText(word as TranscriptTextElement));
        return idx !== undefined ? idx : -1;
      }
      // Fallback for non-text elements (rare): preserve previous behavior
      return this.originalTranscription.findIndex(
        el => el.type === word.type && el.value === word.value,
      );
    });

    // For O(1) membership checks when considering following punctuation
    const activeIndexSet = new Set<number>(activeIndices.filter(i => i !== -1));

    // Find punctuation elements that should be included
    const result: TranscriptElement[] = [];

    activeWords.forEach((word, idx) => {
      result.push(word);

      // Check if there's punctuation following this word
      const wordIndex = activeIndices[idx];
      if (
        wordIndex !== -1 &&
        wordIndex + 1 < this.originalTranscription.length
      ) {
        const nextElement = this.originalTranscription[wordIndex + 1];

        // If the next element is punctuation, include it if this isn't the last word
        // or if the next element is also part of our active words
        if (
          nextElement.type === 'punct' &&
          (idx < activeWords.length - 1 || activeIndexSet.has(wordIndex + 1))
        ) {
          result.push(nextElement);
        }
      }
    });

    return result;
  }

  /**
   * Applies modifications to transcript elements
   */
  private applyModifications(
    elements: TranscriptElement[],
  ): TranscriptElement[] {
    // Fast path: no modifications → return a shallow copy to preserve referential change semantics
    if (this.transcriptModifications.length === 0) {
      return elements.slice();
    }

    // Index modifications by id (word_index string). Preserves insertion order per id.
    const modsById = new Map<string, TranscriptModification[]>();
    for (const mod of this.transcriptModifications) {
      const arr = modsById.get(mod.id);
      if (arr) arr.push(mod);
      else modsById.set(mod.id, [mod]);
    }

    const out: TranscriptElement[] = new Array(elements.length);

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];
      let resultEl: TranscriptElement = element;

      // Lookup modifications only for text elements with word_index
      let mods: TranscriptModification[] | undefined;
      if (element.type === 'text' && element.word_index !== undefined) {
        mods = modsById.get(element.word_index.toString());
      }

      if (mods && mods.length) {
        // Copy-on-write: shallow copy is sufficient for flat elements
        const copy = { ...element } as TranscriptElement;
        for (const mod of mods) {
          switch (mod.type) {
            case 'replaceText':
              if (copy.type === 'text' || copy.type === 'punct') {
                copy.old_value = copy.value;
                copy.value = mod.value;
                copy.state = 'replaced';
              }
              break;
            case 'mute':
              if (copy.type === 'text') {
                if (mod.value === true) {
                  copy.state = 'muted';
                } else if (copy.state === 'muted') {
                  copy.state = undefined;
                }
              }
              break;
            case 'addPunctuation':
              // no-op here
              break;
            case 'removePunctuation':
              if (copy.type === 'punct') {
                copy.state = 'removed';
              }
              break;
          }
        }
        resultEl = copy as TranscriptElement;
      }

      out[i] = resultEl;
    }

    // Filter out elements marked as removed
    return out.filter(el => el.state !== 'removed');
  }

  /**
   * Gets a transcript element by ID
   * @param id ID of the element to find
   * @returns The transcript element, or undefined if not found
   */
  getElementById(id: string): TranscriptElement | undefined {
    return this.originalTranscription.find(
      el => el.initial_index.toString() === id,
    );
  }

  /**
   * Gets a transcript element by word_index
   * @param wordIndex The word_index to find
   * @returns The transcript element, or undefined if not found
   */
  getElementByWordIndex(wordIndex: string): TranscriptElement | undefined {
    return this.originalTranscription.find(
      el => el.type === 'text' && el.word_index?.toString() === wordIndex,
    );
  }

  static isNotRemovedTextElement(
    token: TranscriptElement,
  ): token is TranscriptTextElement & TimelinePositionPropsNotNull {
    return (
      token.type === 'text' &&
      token.state !== 'removed' &&
      'ts' in token &&
      'end_ts' in token
    );
  }
}
